import { useCallback, useMemo, useState } from "react";

import { EmptyState } from "./components/EmptyState";
import { MarketFilter } from "./components/MarketFilter";
import { OpellaHeader } from "./components/OpellaHeader";
import { SelectionBar } from "./components/SelectionBar";
import { VideoCard } from "./components/VideoCard";
import { VideoModal } from "./components/VideoModal";
import { MARKETS, MOCK_VIDEOS, type OpellaVideo } from "./mockData";

import { useUI } from "../../../contexts/ui";
import "./opella.css";

function matchesSearch(video: OpellaVideo, needle: string): boolean {
  if (!needle) return true;
  const haystack = `${video.title} ${video.brand}`.toLowerCase();
  return haystack.includes(needle.toLowerCase());
}

function matchesMarkets(video: OpellaVideo, selected: Set<string>): boolean {
  if (selected.size === 0) return true;
  return video.markets.some((market) => selected.has(market));
}

export function DamPage() {
  const { notify } = useUI();
  const [search, setSearch] = useState("");
  const [selectedMarkets, setSelectedMarkets] = useState<Set<string>>(
    () => new Set(),
  );
  const [selectedVideoIds, setSelectedVideoIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [modalVideoId, setModalVideoId] = useState<string | null>(null);

  const filteredVideos = useMemo(() => {
    return MOCK_VIDEOS.filter(
      (v) => matchesSearch(v, search) && matchesMarkets(v, selectedMarkets),
    );
  }, [search, selectedMarkets]);

  const marketCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const market of MARKETS) {
      counts[market] = MOCK_VIDEOS.filter((v) =>
        v.markets.includes(market),
      ).length;
    }
    return counts;
  }, []);

  const toggleMarket = useCallback((market: string) => {
    setSelectedMarkets((prev) => {
      const next = new Set(prev);
      if (next.has(market)) {
        next.delete(market);
      } else {
        next.add(market);
      }
      return next;
    });
  }, []);

  const selectAllMarkets = useCallback(() => {
    setSelectedMarkets(new Set(MARKETS));
  }, []);

  const clearMarkets = useCallback(() => {
    setSelectedMarkets(new Set());
  }, []);

  const clearAllFilters = useCallback(() => {
    setSearch("");
    setSelectedMarkets(new Set());
  }, []);

  const toggleVideoSelection = useCallback((id: string) => {
    setSelectedVideoIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const clearVideoSelection = useCallback(() => {
    setSelectedVideoIds(new Set());
  }, []);

  const openVideo = useCallback((id: string) => {
    setModalVideoId(id);
  }, []);

  const closeModal = useCallback(() => {
    setModalVideoId(null);
  }, []);

  const activeVideo = useMemo(
    () =>
      modalVideoId
        ? (MOCK_VIDEOS.find((v) => v.id === modalVideoId) ?? null)
        : null,
    [modalVideoId],
  );

  const navigateModal = useCallback(
    (direction: "next" | "prev") => {
      if (!modalVideoId || filteredVideos.length === 0) return;
      const currentIndex = filteredVideos.findIndex(
        (v) => v.id === modalVideoId,
      );
      if (currentIndex === -1) return;
      const delta = direction === "next" ? 1 : -1;
      const nextIndex =
        (currentIndex + delta + filteredVideos.length) % filteredVideos.length;
      setModalVideoId(filteredVideos[nextIndex].id);
    },
    [modalVideoId, filteredVideos],
  );

  const handleSendToReview = useCallback(() => {
    notify({
      title: "Demo action",
      message: `This would send ${selectedVideoIds.size} video(s) to Puntt for review. Not wired in this demo.`,
    });
  }, [notify, selectedVideoIds.size]);

  const handleDownload = useCallback(() => {
    notify({
      title: "Demo action",
      message: `This would package ${selectedVideoIds.size} video(s) for download. Not wired in this demo.`,
    });
  }, [notify, selectedVideoIds.size]);

  return (
    <div className="opella-scope">
      <OpellaHeader
        search={search}
        onSearchChange={setSearch}
        totalCount={MOCK_VIDEOS.length}
        filteredCount={filteredVideos.length}
      />

      <main
        className="mx-auto flex w-full max-w-[1440px]"
        style={{ minHeight: "calc(100vh - 240px)" }}
      >
        <MarketFilter
          markets={MARKETS}
          counts={marketCounts}
          selectedMarkets={selectedMarkets}
          onToggle={toggleMarket}
          onSelectAll={selectAllMarkets}
          onClear={clearMarkets}
        />

        <section className="flex-1 p-10">
          {filteredVideos.length === 0 ? (
            <EmptyState onClear={clearAllFilters} />
          ) : (
            <div
              className="grid gap-6"
              style={{
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              }}
            >
              {filteredVideos.map((video) => (
                <VideoCard
                  key={video.id}
                  video={video}
                  selected={selectedVideoIds.has(video.id)}
                  onToggleSelect={toggleVideoSelection}
                  onPlay={openVideo}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      <SelectionBar
        count={selectedVideoIds.size}
        onClear={clearVideoSelection}
        onSendToReview={handleSendToReview}
        onDownload={handleDownload}
      />

      <VideoModal
        video={activeVideo}
        onClose={closeModal}
        onNavigate={navigateModal}
      />
    </div>
  );
}

export default DamPage;
